/**
 * Saga "Energie door de dag" — one Alpine component holding a single active
 * index. Moment data is read from a sidecar <script type="application/json">
 * (parsed once in init, relative to this component's own root element, not
 * a global id) rather than inlined into the x-data attribute itself — the
 * moment bodies are free-text merchant copy and could contain a quote
 * character that would otherwise break the HTML attribute.
 *
 * The marker's horizontal position is derived from the active moment's own
 * "time" field (hour ÷ 24), not stored separately, so a merchant reordering
 * or retiming blocks can't leave the marker pointing at the wrong hour.
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('xEnergyDay', (initialIndex, colorOff, colorOn) => ({
    active: initialIndex || 0,
    moments: [],
    // Which of the chart's two toggled zone colours/labels is showing —
    // independent of `active` (the moment picker). Defaults to true (the
    // "met Saga" reading), matching this section's original single-scenario
    // behaviour before the toggle existed, so nothing changes for anyone
    // who doesn't touch the switch.
    hasSaga: true,
    colorOff: colorOff || '#dc2626',
    colorOn: colorOn || '#6d8c53',

    init() {
      const script = this.$el.querySelector('[data-saga-ed-data]');
      if (!script) return;
      try {
        this.moments = JSON.parse(script.textContent);
      } catch (e) {
        this.moments = [];
      }
    },

    get current() {
      return this.moments[this.active] || {};
    },

    get markerLeft() {
      const time = this.current.time;
      if (!time) return '0%';
      const parts = time.split(':');
      const hour = parseFloat(parts[0]) + (parseFloat(parts[1] || '0') / 60);
      if (isNaN(hour)) return '0%';
      return (hour / 24) * 100 + '%';
    },

    get scenarioColor() {
      return this.hasSaga ? this.colorOn : this.colorOff;
    },

    // Dutch comma-decimal string ("4,4") -> number. Never throws; blank or
    // unparseable input reads as 0 rather than producing NaN downstream.
    _num(str) {
      if (!str) return 0;
      const n = parseFloat(String(str).replace(',', '.'));
      return isNaN(n) ? 0 : n;
    },

    // number -> Dutch comma-decimal string, one decimal place, matching
    // how every kW value elsewhere in this section is already formatted.
    _fmt(n) {
      return n.toFixed(1).replace('.', ',');
    },

    // Both derived straight from solarKw/usageKw — the same two numbers
    // that already describe the fixed curve at this moment — rather than
    // from separate per-moment/per-scenario settings, so there is no
    // fourth and fifth number someone could type in that drifts out of
    // sync with the curve. Physically: a surplus (solar > usage) either
    // feeds back to the grid (no Saga) or gets captured by the battery
    // (with Saga); a deficit (usage > solar) either draws from the grid
    // (no Saga) or gets covered by the battery (with Saga). "With Saga"
    // isn't literally zero — a small residual, since a real battery still
    // occasionally tops up from or spills to the grid — which is also why
    // this reads as "bijna niets" rather than a suspicious flat 0,0.
    get feedbackKw() {
      const surplus = Math.max(0, this._num(this.current.solarKw) - this._num(this.current.usageKw));
      return this._fmt(this.hasSaga ? surplus * 0.06 : surplus);
    },

    get gridKw() {
      const deficit = Math.max(0, this._num(this.current.usageKw) - this._num(this.current.solarKw));
      return this._fmt(this.hasSaga ? deficit * 0.06 : deficit);
    },
  }));
});
